// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DateTimePicker.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import { isNull } from 'lodash/fp';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Moment from 'moment';
import connect from 'share/enhancers/Izenda.Connect';
import FloatAffixed from 'share/components/controls/Izenda.FloatAffixed';
import Input from 'share/components/controls/Izenda.Input';
import { Types, Units, Keys, Levels } from './Izenda.DateTimeConstants';
import { reduce } from 'lodash/fp';
import DateTimeCalendar from './Izenda.DateTimeCalendar';
import { DATE_TIME_FORMAT, TIME_FORMAT, DATE_FORMAT } from 'logic/data/Izenda.AppGlobalConstants';
import uiState from 'logic/Izenda.UiState';
import { observable } from 'mobx';

const DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

const ISO_REGEX = /((\d{4}-\d\d-\d\d)[tT]([\d:.]*)?)([zZ]|([+-])(\d\d):?(\d\d))/;

@connect({ toClass: true })
export default class DateTimePicker extends Component {
  static propTypes = {
    time: PropTypes.bool,
    calendar: PropTypes.bool,
    min: PropTypes.any,
    max: PropTypes.any,
    format: PropTypes.string,
    onChangeInput: PropTypes.func,
    returnAs: PropTypes.oneOf([Types.ISO, Types.JS_DATE, Types.MOMENT, Types.STRING]),
    closeOnSelect: PropTypes.bool,
    closeOnBlur: PropTypes.bool,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.object,
    disabled: PropTypes.bool,
    visible: PropTypes.bool,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onSelect: PropTypes.func
  };

  static defaultProps = {
    closeOnSelect: true,
    closeOnBlur: true,
    visible: false,
    calendar: true,
    time: true,
    returnAs: Types.STRING
  };

  above: boolean = false;
  @observable datetime: Moment = null;
  @observable input: string = null;
  @observable type = null;
  @observable visible = false;
  @observable level: string = this.getDefaultLevel();

  constructor(props: any) {
    super(props);
    const { datetime, input, type } = this.getDateTimeInput();

    this.datetime = datetime;
    this.input = input;
    this.type = type;

    this.toggleFalse = this.toggle.bind(this, false);
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props != nextProps) {
      const { datetime, input } = this.getDateTimeInput(nextProps);
      this.validate(datetime, null, true);
      this.datetime = datetime;
      this.input = input;
    }
  }

  /**
   * Get date time input
   * @param {Object} props
   */
  getDateTimeInput(props: any) {
    const currentProps = props || this.props;
    const value = currentProps.value || null;
    let datetime, input, type;

    if (isNull(value)) {
      datetime = Moment();
      input = null;
      type = Types.MOMENT;
    } else {
      datetime = this.parse(value);
      input = datetime.format(this.format(currentProps));

      switch (typeof value) {
        case 'object':
          type = Moment.isDate(value) ? Types.JS_DATE : Moment.isMoment(value) ? Types.MOMENT : null;
          break;
        case 'string':
          type = value.match(ISO_REGEX) ? Types.ISO : Types.STRING;
          break;
      }
    }

    return { datetime, input, type };
  }

  /**
   * Get default level
   */
  getDefaultLevel() {
    return this.props.time ? Units.HOUR : Units.DAY;
  }

  /**
   * Get date time string in format
   */
  format(props: any) {
    const currentProps = props || this.props;
    const { displayFormat, calendar, time } = currentProps;
    const dateFormat = uiState.currentUser.dateFormat || DEFAULT_DATE_FORMAT;

    return typeof displayFormat !== 'undefined'
      ? displayFormat
      : calendar === false
      ? TIME_FORMAT
      : time
      ? `${dateFormat} ${TIME_FORMAT}`
      : dateFormat;
  }

  /**
   * Get the format of this date time value
   */
  storeFormat(props: any) {
    const currentProps = props || this.props;
    const { storeFormat, time } = currentProps;

    return typeof storeFormat !== 'undefined' ? storeFormat : !time ? DATE_FORMAT : DATE_TIME_FORMAT;
  }

  toggle(visible: boolean) {
    const { visible: currentVisible } = this;
    if (visible !== currentVisible && !this.props.disabled) {
      if (typeof visible === 'undefined') {
        visible = !currentVisible;
      }

      if (visible !== currentVisible) {
        this.visible = visible;
      }
    }
  }

  /**
   * Parse input to Moment value
   * @param {object} input
   */
  parse(input: any) {
    if (isNull(input)) return null;
    let value = null;
    const formats = [this.storeFormat(), uiState.currentUser.dateFormat, DATE_TIME_FORMAT, DATE_FORMAT, TIME_FORMAT];

    // Try parse string to date time by each format, and set the value at the first one that parse successfully
    const tryParseFormats = () => {
      return reduce(
        (parseResult, format) => {
          // Ignore if the input had been parsed successfully before
          if (parseResult) return true;

          const test = Moment(input, format, true);
          test.isValid() && (value = test);
          return test.isValid();
        },
        false,
        formats
      );
    };

    const result = tryParseFormats();
    return result ? value : Moment(this.datetime || Moment());
  }

  /**
   * Save the date time value
   * @param {Moment} savingValue
   */
  save(savingValue: Moment) {
    this.datetime = savingValue;
    this.input = savingValue.format(this.format());

    this.validate(savingValue, null, true) && this.commit(savingValue);
  }

  /**
   * Validate the date time
   */
  validate = (datetime, timeUnit) => {
    const { min, max } = this.props;
    const isOutRange = (min && Moment(datetime).isBefore(min)) || (max && Moment(datetime).isAfter(max));

    return !(
      isOutRange &&
      timeUnit !== 'hours' &&
      (Moment(datetime).isSame(min, timeUnit) || Moment(datetime).isSame(max, timeUnit))
    );
  };

  /**
   * Notify the change
   * @param {Moment} datetime
   */
  commit(datetime: Moment) {
    const returnAs = this.props.returnAs || this.type;
    const getResult = () => {
      switch (returnAs) {
        case Types.ISO:
          return datetime.toISOString();
        case Types.JS_DATE:
          return datetime.toDate();
        case Types.MOMENT:
          return datetime;
        case Types.STRING:
          return Moment(datetime).format(this.storeFormat());
      }
    };

    this.props.onChange && this.props.onChange(getResult());
  }

  /**
   * On blur input
   */
  onBlurInput = (e: Event) => {
    const { closeOnBlur } = this.props;
    const { input, datetime } = this;

    if (this.above) {
      this.inputNode && this.inputNode.focus();
    } else if (closeOnBlur) {
      this.toggle(false);
      this.props.onBlur && this.props.onBlur(e);
    }

    if (input != datetime.format(this.format())) {
      const savingData = this.parse(input);
      savingData && this.save(savingData);
    }
  };

  /**
   * On change input
   */
  onChangeInput = (e: Event) => {
    this.props.onChangeInput && this.props.onChangeInput(e);

    const { value: input } = e.target;
    const datetime = Moment(input, this.format(), true);
    if (datetime.isValid()) {
      this.save(datetime);
    } else if (input === '') {
      this.datetime = null;
      this.input = '';
      this.props.onChange && this.props.onChange(null);
    } else {
      this.input = input;
    }
  };

  /**
   * On select new date time value
   */
  onSelect = (datetime, close, timeUnit) => {
    let shouldClose = close;
    const { visible } = this;
    const { closeOnSelect } = this.props;

    if ((timeUnit && !this.validate(datetime, timeUnit.unit)) || !this.validate(datetime)) {
      shouldClose = false;
    }

    if (close && shouldClose === false) return;

    const willBeVisible = closeOnSelect && shouldClose ? !visible : visible;

    this.visible = willBeVisible;
    this.save(datetime);

    this.props.onSelect && this.props.onSelect(datetime, willBeVisible);
  };

  /**
   * On key down input
   */
  onKeyDown = (e: Event) => {
    const { keyCode } = e;
    const { level, input, visible } = this;
    const datetime = this.datetime || Moment();
    const lvl = Levels[level];

    switch (keyCode) {
      case Keys.UP:
        this.onSelect(datetime.subtract(lvl.key.span, lvl.key.unit));
        break;
      case Keys.DOWN:
        this.onSelect(datetime.add(lvl.key.span, lvl.key.unit));
        break;
      case Keys.ENTER:
        if (lvl.down) {
          this.level = lvl.down;
        } else {
          if (input == datetime.format(this.format()) && !this.validate(datetime)) {
            this.toggle(true);
          } else {
            !visible && this.toggle(true);
            const savingValue = this.parse(input);
            savingValue && this.save(savingValue);
          }
        }
        break;
    }
  };

  /**
   * On click calendar icon
   */
  onCalendarClick = () => {
    if (!this.props.disabled) {
      this.inputNode.focus();
      this.level = Units.DAY;
      this.visible = true;
    }
  };

  /**
   * On clock time icon
   */
  onTimeClick = () => {
    if (!this.props.disabled) {
      this.inputNode.focus();
      this.level = Units.HOUR;
      this.visible = true;
    }
  };

  handleAbove = value => {
    return typeof value === 'undefined' ? this.above : (this.above = value);
  };

  setLevel = (level: string) => {
    this.level = level;
  };

  /**
   * Set dom node to the input element in Input Component
   */
  setDomNode = inputNode => {
    inputNode && (this.inputNode = inputNode.input);
  };

  /**
   * Render calendar and time button
   */
  renderControlButtons() {
    return (
      <div className='izenda-DateTimePicker-controls'>
        {this.props.calendar && (
          <button className='izenda-DateTimePicker-button' onClick={this.onCalendarClick}>
            <span className='icon-calendar-new' />
          </button>
        )}
        {this.props.time && (
          <button className='izenda-DateTimePicker-button' onClick={this.onTimeClick}>
            <span className='icon-clock' />
          </button>
        )}
      </div>
    );
  }

  render() {
    const { className, calendar, time, name, disabled, placeholder, id, options } = this.props;
    const { input, datetime, visible, level } = this;

    const mainClasses = classnames(className, 'izenda-DateTimePicker', {
      'once-control': !(calendar && time)
    });

    return (
      <div className={mainClasses}>
        <Input
          type='text'
          ref={this.setDomNode}
          value={input || ''}
          onBlur={this.onBlurInput}
          onKeyDown={this.onKeyDown}
          onChange={this.onChangeInput}
          placeholder={placeholder}
          name={name}
          className='izenda-DateTimePicker-input'
          disabled={disabled}
        />
        {this.renderControlButtons()}
        {visible && (
          <FloatAffixed anchor={this.inputNode} onClickOutside={this.toggleFalse} onParentScroll={this.toggleFalse}>
            <DateTimeCalendar
              id={id}
              datetime={datetime}
              onSelect={this.onSelect}
              above={this.handleAbove}
              level={level}
              setLevel={this.setLevel}
              validate={this.validate}
              options={options}
            />
          </FloatAffixed>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/DateTime/Izenda.DateTimePicker.js